import React from 'react';
import { motion } from 'framer-motion';
import { Globe, ShieldCheck, Zap, Users } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function AboutPage() {
  const {
    t
  } = useLanguage();
  return <div className="w-full bg-white pb-32" data-id="element-126">
      {/* Hero */}
      <div className="relative h-[55vh] md:h-[60vh] w-full flex items-center justify-center overflow-hidden" data-id="element-127">
        <div className="absolute inset-0 z-0" data-id="element-128">
          <img src="https://cdn.magicpatterns.com/uploads/qS64CvcJQa1nCJvnKZC5xd/1_imagen_hero_.png" alt="Empresa" className="w-full h-full object-cover object-bottom" data-id="element-129" />
          <div className="absolute inset-0 bg-corporate-dark/80" data-id="element-130"></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-16" data-id="element-131">
          <motion.h1 initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-8 tracking-tight" data-id="element-132">
            {t('about.hero.title')}
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="text-2xl text-gray-200 font-light leading-relaxed max-w-3xl mx-auto" data-id="element-133">
            {t('about.hero.desc')}
          </motion.p>
        </div>
      </div>

      {/* Story Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-36" data-id="element-134">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-135">
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-136">
            <h2 className="text-4xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-137">
              {t('about.vision.title')}
            </h2>
            <div className="w-20 h-0.5 bg-accent-beige mb-10" data-id="element-138"></div>
            <div className="space-y-8 text-gray-600 leading-relaxed text-lg font-light" data-id="element-139">
              <p data-id="element-140">{t('about.vision.p1')}</p>
              <p data-id="element-141">{t('about.vision.p2')}</p>
              <p data-id="element-142">{t('about.vision.p3')}</p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 40
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="relative group" data-id="element-143">
            <div className="aspect-square bg-neutral-grey rounded-sm overflow-hidden p-8 transition-transform duration-700 ease-premium group-hover:scale-[1.02]" data-id="element-144">
              <img src="https://cdn.magicpatterns.com/uploads/dGu33eyB5j7rueds66611Z/Logo_smartimport-1.png" alt="SmartImport Logo" className="w-full h-full object-contain opacity-80" data-id="element-145" />
            </div>
            <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-corporate-blue/10 rounded-full -z-10 blur-3xl transition-all duration-700 group-hover:bg-corporate-blue/20" data-id="element-146"></div>
            <div className="absolute -top-8 -right-8 w-80 h-80 bg-accent-beige/10 rounded-full -z-10 blur-3xl transition-all duration-700 group-hover:bg-accent-beige/20" data-id="element-147"></div>
          </motion.div>
        </div>
      </div>

      {/* Pillars */}
      <div className="bg-neutral-grey py-28 lg:py-36" data-id="element-148">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-149">
          <div className="text-center mb-20" data-id="element-150">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-corporate-dark mb-6 tracking-tight" data-id="element-151">
              {t('about.pillars.title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-xl font-light leading-relaxed" data-id="element-152">
              {t('about.pillars.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10" data-id="element-153">
            {[{
            icon: Zap,
            title: t('about.pil1.title'),
            desc: t('about.pil1.desc')
          }, {
            icon: ShieldCheck,
            title: t('about.pil2.title'),
            desc: t('about.pil2.desc')
          }, {
            icon: Globe,
            title: t('about.pil3.title'),
            desc: t('about.pil3.desc')
          }, {
            icon: Users,
            title: t('about.pil4.title'),
            desc: t('about.pil4.desc')
          }].map((pillar, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.15,
            duration: 0.8,
            ease: [0.25, 0.1, 0.25, 1]
          }} className="bg-white p-10 rounded-sm shadow-sm border border-gray-100 text-center hover:shadow-xl hover:-translate-y-2 transition-all duration-500 ease-premium" data-id="element-154">
                <div className="mx-auto w-16 h-16 bg-corporate-blue/5 rounded-full flex items-center justify-center mb-8" data-id="element-155">
                  <pillar.icon className="h-8 w-8 text-corporate-blue" data-id="element-156" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-corporate-dark mb-4 tracking-tight" data-id="element-157">
                  {pillar.title}
                </h3>
                <p className="text-gray-600 text-base font-light leading-relaxed" data-id="element-158">
                  {pillar.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </div>;
}